import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Gift, Users } from 'lucide-react';
import { adminGrant } from '../../api';

export default function GrantShares({ token, users, companies, onGranted }) {
  const [selectedUser, setSelectedUser] = useState('');
  const [company, setCompany] = useState('');
  const [shares, setShares] = useState('');
  const [costBasis, setCostBasis] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (users.length && !selectedUser) setSelectedUser(users[0].id);
  }, [users, selectedUser]);
  useEffect(() => {
    if (companies.length && !company) setCompany(companies[0].id);
  }, [companies, company]);

  const activeCompany = companies.find((c) => c.id === company);

  const submit = async () => {
    const n = parseInt(shares || '0');
    if (!selectedUser || n <= 0) {
      toast.error('Select a user and enter share count');
      return;
    }
    setBusy(true);
    try {
      await adminGrant(token, {
        userId: selectedUser,
        companyId: company,
        shares: n,
        costBasis: costBasis ? parseFloat(costBasis.replace(/[^0-9.]/g, '')) : null,
        note: note || null,
      });
      const c = companies.find((x) => x.id === company);
      toast.success('Shares granted', {
        description: `${n.toLocaleString()} ${c.ticker} credited to investor ${selectedUser.slice(0, 8)}…`,
      });
      setShares('');
      setCostBasis('');
      setNote('');
      onGranted && onGranted();
    } catch {
      toast.error('Grant failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="rounded-xl bg-[var(--panel)] border border-[var(--border)] p-6">
        <div className="flex items-center gap-2">
          <Gift size={16} className="text-[var(--orange)]" />
          <h3 className="font-display font-semibold text-[15px] text-white">GRANT SHARES TO USER</h3>
        </div>
        <p className="text-[12.5px] text-[var(--muted)] mt-2 leading-relaxed">
          Manually credit shares to a user (e.g. for funds collected earlier). This adds to their holdings without touching their vault.
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">SELECT USER</label>
            <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-[13px] text-white focus:outline-none focus:border-[var(--orange)] transition-colors">
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.kyc} · {u.id.slice(0, 8)}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">COMPANY</label>
              <select value={company} onChange={(e) => setCompany(e.target.value)} className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-[13px] text-white focus:outline-none focus:border-[var(--orange)] transition-colors">
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.ticker} — {c.name.slice(0, 16)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">NUMBER OF SHARES</label>
              <input value={shares} onChange={(e) => setShares(e.target.value.replace(/[^0-9]/g, ''))} placeholder="1000" className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 font-mono text-[14px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--orange)] transition-colors" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">COST BASIS / SHARE (OPTIONAL)</label>
              <input value={costBasis} onChange={(e) => setCostBasis(e.target.value)} placeholder={activeCompany ? `$${activeCompany.lastPrice.toFixed(2)} (last)` : '$0.00'} className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 font-mono text-[14px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--orange)] transition-colors" />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">NOTE (OPTIONAL)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Pre-funded allocation" className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-[13px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--orange)] transition-colors" />
            </div>
          </div>
          <button onClick={submit} disabled={busy} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[var(--green)] text-[#052e20] font-semibold text-[14px] hover:bg-[#13b878] transition-colors disabled:opacity-60">
            <Gift size={16} /> {busy ? 'GRANTING…' : 'GRANT SHARES'}
          </button>
        </div>
      </div>

      <div className="rounded-xl bg-[var(--panel)] border border-[var(--border)] p-6">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-[var(--muted)]" />
          <h3 className="font-display font-semibold text-[15px] text-white">USERS ({users.length})</h3>
        </div>
        <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg)] border border-[var(--border-soft)]">
              <div>
                <div className="text-[13px] text-white">{u.name}</div>
                <div className="text-[10.5px] font-mono text-[var(--muted-2)]">{u.id.slice(0, 8)}…</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[13px] text-[var(--green)]">${u.wallet.toLocaleString()}</div>
                <div className={`text-[9px] font-mono tracking-widest ${u.kyc === 'verified' ? 'text-[var(--green)]' : 'text-[var(--muted-2)]'}`}>{u.kyc.toUpperCase()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
